// Routing Flow — bot→bot (and bot→human) transition matrix.
//
// For multi-bot orchestrations like Prisma Ortho (OrthoMain router +
// 5 task bots) the single most useful question a supervisor asks is
// "when OrthoMain hands off, where does it actually go?" This report
// answers it directly: rows = (fromBot, toBot) pairs, columns = count,
// share of fromBot's handoffs, success-rate at the destination.
//
// Data: pure client-side join on interactionId using the shared
// sibling-session detection in lib/sessionClassification.js. Zero new
// MCP calls — reuses the sessions + tasks arrays useReportWindow fetches
// for every Analytics tab.
//
// Bot → human edges show up as destination = "__HUMAN__" (rendered as
// a "Human handoff" badge). Terminal task bots that complete via tools
// don't produce an edge — they're captured in BotEffectiveness, not here.

import React, { useMemo, useState } from 'react';
import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';
import {
  classifySessions,
  rollupByBot,
  transitionsFromSessions,
  ROLE_META,
} from '../lib/sessionClassification';

const REPORT_ID = 'routing-flow';

const HUMAN_SINK = '__HUMAN__';

const COLUMNS = [
  {
    id: 'from',
    label: 'From (bot)',
    pinned: true,
    render: (r) => (
      <Group gap={6}>
        <Text size="sm" fw={500}>{r.fromBot}</Text>
        {r.fromRole && <RoleBadge role={r.fromRole} />}
      </Group>
    ),
  },
  {
    id: 'to',
    label: 'To',
    pinned: true,
    render: (r) => (
      r.toBot === HUMAN_SINK
        ? <Badge size="sm" color="blue" variant="light">Human handoff</Badge>
        : <Text size="sm">{r.toBot}</Text>
    ),
  },
  {
    id: 'count',
    label: 'Transitions',
    hint: 'Number of (fromBot → toBot) handoffs observed in this window.',
    align: 'right',
    render: (r) => <Text size="sm" fw={600}>{r.count}</Text>,
  },
  {
    id: 'share',
    label: '% of from-bot handoffs',
    hint: 'This destination\'s share of fromBot\'s TOTAL outbound handoffs (sub-bot + human combined). All rows for a given fromBot sum to 100%.',
    align: 'right',
    render: (r) => (
      <Tooltip label={`${r.count} of ${r.fromTotalHandoffs} handoffs from ${r.fromBot}`}>
        <Badge
          size="sm"
          color={r.toBot === HUMAN_SINK ? 'blue' : 'cyan'}
          variant={r.share >= 20 ? 'filled' : 'light'}
        >
          {r.share}%
        </Badge>
      </Tooltip>
    ),
  },
  {
    id: 'shareBar',
    label: 'Share bar',
    hint: 'Visual share-of-handoffs for the row relative to the fromBot row with the largest count.',
    width: 160,
    render: (r) => (
      <Progress value={r.share} size="sm" color={r.toBot === HUMAN_SINK ? 'blue' : 'cyan'} />
    ),
  },
];

const DEFAULT_VISIBLE = ['count', 'share', 'shareBar'];

// `analysis` (optional) — pre-computed { perBot, classified } from Analytics.jsx.
// The role badge uses analysis.perBot so this report's role tags match what
// BotEffectiveness shows for the same bot. Falls back to inline compute.
//
// `onViewSession` (optional) — enables row-click drill. Clicking a
// (fromBot → toBot) row opens a modal showing the fromBot sessions whose
// handoff landed on that destination.
export function RoutingFlow({ data, analysis, onViewSession, window }) {
  const sessions = data?.sessions ?? [];
  const tasks = data?.tasks ?? [];
  const rows = useMemo(
    () => buildRows(sessions, tasks, analysis?.perBot),
    [sessions, tasks, analysis?.perBot],
  );
  const sessionsByTransition = useMemo(() => groupSessionsByTransition(sessions, tasks), [sessions, tasks]);

  const fromCount = new Set(rows.map((r) => r.fromBot)).size;
  const totalTransitions = rows.reduce((n, r) => n + r.count, 0);
  const humanShare = rows
    .filter((r) => r.toBot === HUMAN_SINK)
    .reduce((n, r) => n + r.count, 0);
  const humanPct = totalTransitions > 0 ? Math.round((humanShare / totalTransitions) * 100) : 0;

  const [drill, setDrill] = useState(null); // { fromBot, toBot }

  const csvRows = useMemo(() => rows.map(toCsvRow), [rows]);
  const csvFilename = `routing-flow-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <>
      <ReportTable
        title="Routing flow — bot → bot (and bot → human) transitions"
        subtitle={
          `${totalTransitions} handoff${totalTransitions === 1 ? '' : 's'} across ${fromCount} from-bot${fromCount === 1 ? '' : 's'} · ` +
          `${humanPct}% went to a human. ` +
          `Rows per fromBot sum to 100%; a fromBot that always routes to one sub-bot is a strict router, ` +
          `a fromBot with mixed destinations is a classifier.` +
          (onViewSession ? ' Click a row to see the from-bot sessions behind that transition.' : '')
        }
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No multi-bot handoffs or human escalations recorded in this window."
        onRowClick={onViewSession ? (r) => setDrill({ fromBot: r.fromBot, toBot: r.toBot }) : undefined}
        csvExport={{ filename: csvFilename, rows: csvRows }}
      />
      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.fromBot} → ${drill.toBot === HUMAN_SINK ? 'Human handoff' : drill.toBot}` : ''}
        subtitle={drill ? `From-bot sessions whose handoff landed on this destination` : ''}
        sessions={drill ? (sessionsByTransition.get(`${drill.fromBot}||${drill.toBot}`) ?? []) : []}
        onViewSession={onViewSession}
        window={window}
      />
    </>
  );
}

// For each transition we observed, keep the FROM-bot session objects so the
// drill modal can list them. Same walking logic as transitionsFromSessions
// but we retain the source session instead of just incrementing an edge count.
function groupSessionsByTransition(sessions, tasks) {
  const taskByInt = new Map();
  for (const t of tasks ?? []) if (t?.id) taskByInt.set(t.id, t);

  const siblingsByInt = new Map();
  for (const s of sessions ?? []) {
    const key = s?.interactionId;
    if (!key) continue;
    if (!siblingsByInt.has(key)) siblingsByInt.set(key, []);
    siblingsByInt.get(key).push(s);
  }
  for (const list of siblingsByInt.values()) {
    list.sort((a, b) => (Number(a?.sessionStartTime) || 0) - (Number(b?.sessionStartTime) || 0));
  }

  const m = new Map();
  const push = (fromBot, toBot, session) => {
    const key = `${fromBot}||${toBot}`;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(session);
  };

  for (const list of siblingsByInt.values()) {
    for (let i = 0; i < list.length; i += 1) {
      const cur = list[i];
      if (cur?.sessionEndTime == null) continue;
      const curBot = cur?.agentName ?? '(unnamed)';
      const next = list[i + 1];
      if (next) {
        push(curBot, next?.agentName ?? '(unnamed)', cur);
        continue;
      }
      if (cur?.interactionOutcome === 'ESCALATED') {
        push(curBot, HUMAN_SINK, cur);
        continue;
      }
      const task = taskByInt.get(cur?.interactionId);
      if (task?.lastAgent?.name) push(curBot, HUMAN_SINK, cur);
    }
  }
  return m;
}

function toCsvRow(r) {
  return {
    from_bot: r.fromBot,
    to_bot: r.toBot === HUMAN_SINK ? 'HUMAN' : r.toBot,
    transitions: r.count,
    share_of_from_bot_pct: r.share,
    from_role: r.fromRole ?? '',
  };
}

function buildRows(sessions, tasks, perBotOverride) {
  const edges = transitionsFromSessions(sessions, tasks);
  if (edges.length === 0) return [];

  // Per-bot totals so each row can express share of ITS fromBot's handoffs.
  const totalByFrom = new Map();
  for (const e of edges) {
    totalByFrom.set(e.fromBot, (totalByFrom.get(e.fromBot) ?? 0) + e.count);
  }

  // Role tag per fromBot — same role model as the scorecards use, so the
  // Role badge here matches what BotEffectiveness shows for the same bot.
  const rolled = perBotOverride ?? rollupByBot(classifySessions(sessions, tasks));
  const roleByBot = new Map(rolled.map((b) => [b.name, b.role]));

  return edges
    .map((e) => ({
      key: `${e.fromBot}||${e.toBot}`,
      fromBot: e.fromBot,
      toBot: e.toBot,
      count: e.count,
      fromTotalHandoffs: totalByFrom.get(e.fromBot) ?? e.count,
      share: pct(e.count, totalByFrom.get(e.fromBot) ?? e.count),
      fromRole: roleByBot.get(e.fromBot) ?? null,
    }))
    .sort((a, b) => (
      a.fromBot === b.fromBot
        ? b.count - a.count
        : (totalByFrom.get(b.fromBot) ?? 0) - (totalByFrom.get(a.fromBot) ?? 0) || a.fromBot.localeCompare(b.fromBot)
    ));
}

function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }

function RoleBadge({ role }) {
  const meta = ROLE_META[role] ?? ROLE_META.unknown;
  return (
    <Tooltip label={meta.description}>
      <Badge size="xs" color={meta.color} variant="light">{meta.label}</Badge>
    </Tooltip>
  );
}
